import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useOverlayActions, useOverlays } from '../../contexts/StoreContext'
import { useCatalog } from '../../contexts/CatalogContext'
import { useAccount } from '../../contexts/AccountContext'
import { useSubmit } from '../../hooks/useSubmit'
import { ApiError, fetchAvailability, isAbortError, newRequestId, submitBooking } from '../../utils/api'
import { courseStatus } from '../../utils/account'
import { useSite } from '../../contexts/SiteContext'
import { FALLBACK_DURATION, availability } from '../../core/rules/agenda'
import { OWNER_STAFF_ID, canPerform, hasColleagueDays, regularPerformer, staffList, staffName } from '../../core/rules/salonDays'
import { fromIso, iso } from '../../core/format/dates'
import { isValidEmail } from '../../core/rules/contact'
import { useFeatures } from '../../hooks/useFeatures'
import { track } from '../../utils/analytics'
import { normalizeSource } from '../../core/rules/acquisition'
import type { BookingRequest } from '../../core/types/requests'
import type { AcquisitionSource } from '../../core/types/insights'
import type { AvailabilityDay } from '../../core/types/agenda'

/** Zoveel open dagen kan een klant in de lade kiezen. */
const BOOKABLE_DAYS = 6

/**
 * Alles achter de afsprakenlade: gekozen behandeling, dag en tijd, gastgegevens,
 * de kuursessie van een ingelogde klant en het versturen (één id per aanvraag,
 * zodat dubbel klikken geen tweede afspraak maakt).
 */
export function useBooking() {
  const { bookingOpen, bookingTreatment } = useOverlays()
  const { closeBooking } = useOverlayActions()
  const { treatments, treatmentBySlug, routineForTreatment, routineTotals } = useCatalog()
  // Ingelogd? Dan kennen we naam, e-mail, of ze al eens geweest is en waar ze in haar kuur zit.
  const { account, requestAppointment } = useAccount()
  // Openingstijden voor de terugval: via een ref, zodat een nieuwe stand niet opnieuw laat ophalen.
  const { openingHours, salonExceptions, staff: rawStaff } = useSite()
  const team = useMemo(() => staffList(rawStaff), [rawStaff])
  const hoursRef = useRef({ openingHours, salonExceptions, team })
  hoursRef.current = { openingHours, salonExceptions, team }
  // Gedeelde salon: alleen dan "met wie" per dag en de keuze "Bij wie wil je komen?".
  const shared = useMemo(() => hasColleagueDays(openingHours, salonExceptions), [openingHours, salonExceptions])
  /** `undefined` = nog niet zelf gekozen (dan geldt de standaard); `null` = "Maakt niet uit". */
  const [chosenStaff, setChosenStaff] = useState<string | null | undefined>(undefined)

  // Geen behandelingen in de catalogus (bijv. alles tijdelijk offline gezet)? Dan geen crash.
  const [treatment, setTreatment] = useState(treatments[0]?.slug ?? '')
  const [dayDate, setDayDate] = useState<string | null>(null)
  const [time, setTime] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [firstVisit, setFirstVisit] = useState(false)
  /** "Hoe kwam je bij ons?": alleen voor een gast bij een eerste bezoek, en alleen als het onderdeel aan staat. */
  const [source, setSource] = useState<AcquisitionSource | null>(null)
  const { acquisitionQuestion } = useFeatures()
  const [emailTouched, setEmailTouched] = useState(false)
  const [requestId, setRequestId] = useState(newRequestId)

  const course = useMemo(() => (account ? courseStatus(account) : null), [account])
  const sessionFor = useCallback((slug: string) => (course && course.course.treatmentSlug === slug ? course.nextToBook : undefined), [course])

  // Wie kan deze behandeling doen? Alleen die staan in de keuze. Eén persoon = geen keuze nodig.
  const performers = useMemo(() => team.filter((s) => s.active && canPerform(team, s.id, treatment)), [team, treatment])
  // Lopende kuur of vaste behandelaar: die staat standaard aan (zelf wijzigen kan altijd).
  const regular = useMemo(
    () => (shared && account ? regularPerformer(course?.course, account.visits, treatment, team) : null),
    [shared, account, course, treatment, team],
  )
  const picked = chosenStaff === undefined ? regular : chosenStaff
  const staffFilter = shared && picked && performers.some((s) => s.id === picked) ? picked : null

  // Vrije tijden uit de agenda van de salon (de server weet wat er al geboekt is).
  // Lukt ophalen niet, dan als terugval dezelfde rekenregel als de server (core/rules/agenda),
  // maar zonder de afspraken en blokkades die we niet kennen: starttijden waarbij de behandeling
  // met haar échte duur vóór sluiten klaar is. De server weigert een bezette tijd alsnog (409).
  const [available, setAvailable] = useState<AvailabilityDay[] | null>(null)
  const [availabilityKey, setAvailabilityKey] = useState(0)
  // Terugval actief: de klant ziet dat een tijd nog bezet kan blijken, in plaats van dat pas te merken bij versturen.
  const [approximate, setApproximate] = useState(false)
  const duration = treatmentBySlug(treatment)?.durationMinutes ?? FALLBACK_DURATION
  useEffect(() => {
    if (!bookingOpen || !treatment) return
    const controller = new AbortController()
    setAvailable(null)
    setApproximate(false)
    fetchAvailability(treatment, staffFilter ? { staff: staffFilter } : undefined, { signal: controller.signal })
      .then((daysFree) => !controller.signal.aborted && setAvailable(daysFree))
      .catch((err: unknown) => {
        if (controller.signal.aborted || isAbortError(err)) return
        const h = hoursRef.current
        setAvailable(
          availability(h.openingHours, BOOKABLE_DAYS, duration, [], [], {
            exceptions: h.salonExceptions,
            canDo: (id) => canPerform(h.team, id, treatment),
            ...(staffFilter ? { staffId: staffFilter } : {}),
          }),
        )
        setApproximate(true)
      })
    return () => controller.abort()
  }, [bookingOpen, treatment, duration, availabilityKey, staffFilter])

  const send = useCallback(
    async (req: BookingRequest) => {
      try {
        if (!account) {
          const answer = await submitBooking(req)
          track('afspraak-aangevraagd', { behandeling: req.treatmentSlug })
          return answer
        }
        const ok = await requestAppointment({
          treatmentSlug: req.treatmentSlug,
          date: req.date,
          time: req.time,
          session: sessionFor(req.treatmentSlug),
          ...(req.staffId ? { staffId: req.staffId } : {}),
        })
        if (!ok) {
          // De account-rij toont de precieze reden al als melding (bijv. "net bezet", of "je bent
          // uitgelogd") en geeft alleen ja/nee terug. Dus hier geen eigen reden raden: alleen de
          // tijden verversen, zodat een bezette tijd nooit blijft staan.
          setTime(null)
          setAvailabilityKey((k) => k + 1)
          throw new ApiError('Je aanvraag ging niet door; de reden staat in de melding. Kies een tijd en probeer het opnieuw.')
        }
        track('afspraak-aangevraagd', { behandeling: req.treatmentSlug })
        return { bookingId: 'account' }
      } catch (err) {
        // Net door iemand anders geboekt: tijden verversen en opnieuw laten kiezen.
        if (err instanceof ApiError && err.code === 'slot_taken') {
          setTime(null)
          setAvailabilityKey((k) => k + 1)
        }
        throw err
      }
    },
    [account, requestAppointment, sessionFor],
  )
  const booking = useSubmit(send)

  // De gekozen dag op datum bewaren, niet op positie: na verversen kan een volle dag wegvallen.
  const days = useMemo(() => (available ?? []).map((d) => fromIso(d.date)), [available])
  const foundIndex = available && dayDate ? available.findIndex((d) => d.date === dayDate) : -1
  const dayIndex = foundIndex >= 0 ? foundIndex : null
  const day = dayIndex !== null ? days[dayIndex] : undefined
  const slots = useMemo(() => (dayIndex !== null ? (available?.[dayIndex]?.times ?? []) : []), [available, dayIndex])
  const slotsLoading = bookingOpen && available === null

  // Andere behandeling = andere duur = andere vrije tijden.
  useEffect(() => {
    setTime(null)
  }, [treatment])

  useEffect(() => {
    if (!bookingOpen) return
    setTreatment(bookingTreatment ?? treatments[0]?.slug ?? '')
    if (booking.succeeded) {
      // Nieuwe aanvraag na een eerdere: schoon beginnen met een nieuw id.
      booking.reset()
      setRequestId(newRequestId())
      setDayDate(null)
      setTime(null)
      setSource(null)
      setChosenStaff(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij het openen van de lade, niet bij elke wijziging van de aanvraag
  }, [bookingOpen, bookingTreatment])

  const selected = treatmentBySlug(treatment)
  const upsell = selected ? routineForTreatment(selected.slug) : undefined
  const upsellTotals = upsell ? routineTotals(upsell) : null
  const session = sessionFor(treatment)
  const signedIn = Boolean(account)
  const knownFirstVisit = account ? account.visits.length === 0 : firstVisit
  // Ingelogd: de server kent haar al (en vraagt het op de bedankpagina of aan de kassa).
  const askSource = acquisitionQuestion && !account && firstVisit
  const sourceToSend = askSource ? normalizeSource(source) : null
  const emailValid = isValidEmail(email)
  // Pas een fout tonen na het verlaten van het veld, niet tijdens het typen.
  const emailInvalid = !signedIn && emailTouched && email.trim() !== '' && !emailValid
  const canSubmit = day !== undefined && time !== null && (signedIn || (name.trim() !== '' && emailValid))

  const pickDay = (i: number) => {
    setDayDate(available?.[i]?.date ?? null)
    setTime(null)
  }

  // Een gekozen tijd die na verversen niet meer vrij is, loslaten.
  useEffect(() => {
    if (time !== null && !slots.includes(time)) setTime(null)
  }, [time, slots])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit || !day || time === null) return
    void booking.submit({
      requestId,
      treatmentSlug: treatment,
      date: iso(day),
      time,
      name: account ? `${account.customer.firstName} ${account.customer.lastName}`.trim() : name.trim(),
      email: account ? account.customer.email : email.trim(),
      firstVisit: knownFirstVisit,
      ...(sourceToSend ? { acquisitionSource: sourceToSend } : {}),
      // Gekozen behandelaar: de server weigert een dag waarop zij niet werkt (422).
      ...(staffFilter ? { staffId: staffFilter } : {}),
    })
  }

  // Wie het wordt: de persoon van de gekozen dag (de dag bepaalt de uitvoerder).
  const performerName = shared && dayIndex !== null ? staffName(team, available?.[dayIndex]?.staffId) : ''
  // Na versturen bewaard voor de bevestiging (de lade kan intussen opnieuw ophalen).
  const [confirmedWith, setConfirmedWith] = useState('')
  useEffect(() => {
    if (booking.submitting) setConfirmedWith(performerName)
  }, [booking.submitting, performerName])

  return {
    open: bookingOpen,
    close: closeBooking,
    account,
    signedIn,
    treatments,
    treatment,
    setTreatment,
    selected,
    course,
    session,
    days,
    dayIndex,
    day,
    pickDay,
    /** Dag zonder vrije tijd: zichtbaar (de week klopt), maar niet te kiezen. */
    dayFull: (i: number) => available?.[i]?.times.length === 0,
    /** Gedeelde salon: wie er op die dag behandelt ("met Mila"). Leeg als er maar één persoon is. */
    dayStaff: (i: number) => (shared ? staffName(team, available?.[i]?.staffId) : ''),
    shared,
    /** Wie deze behandeling doet; alleen als er iets te kiezen valt (gedeelde salon, meer dan één). */
    performers: shared && performers.length > 1 ? performers : [],
    /** Gekozen behandelaar, of `null` = "Maakt niet uit (eerst beschikbaar)". */
    staffChoice: staffFilter,
    /** Kwam de keuze uit de kuur of de vaste behandelaar (voor de uitleg erbij)? */
    staffFromHistory: chosenStaff === undefined && staffFilter !== null,
    /**
     * Kuursessie bij een ander dan bij wie de kuur loopt: een melding, geen blokkade. Op basis van
     * de gekozen persoon, of anders wie er op de gekozen dag werkt.
     */
    courseStaffWarning: (() => {
      if (!shared || !course || course.finished || course.course.treatmentSlug !== treatment) return ''
      const own = course.course.staffId || OWNER_STAFF_ID
      const performer = staffFilter ?? (dayIndex != null ? available?.[dayIndex]?.staffId : undefined)
      if (!performer || performer === own) return ''
      return `Je kuur loopt bij ${staffName(team, own)}. Deze sessie doet ${staffName(team, performer)}; dat kan gewoon, ze werken met hetzelfde plan.`
    })(),
    setStaffChoice: (id: string | null) => {
      setChosenStaff(id)
      setDayDate(null)
      setTime(null)
    },
    performerName,
    confirmedWith,
    slots,
    slotsLoading,
    approximate,
    time,
    setTime,
    name,
    setName,
    email,
    setEmail,
    setEmailTouched,
    emailInvalid,
    firstVisit,
    setFirstVisit,
    knownFirstVisit,
    askSource,
    source,
    setSource,
    upsell,
    upsellTotals,
    canSubmit,
    submitting: booking.submitting,
    error: booking.error,
    done: booking.succeeded,
    submit,
  }
}

export type BookingControl = ReturnType<typeof useBooking>
